import React from 'react'
import { Alert, Button, Group, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'

import { CrFormHandle } from '@/components/Event/CrForm/CrFormStep'
import { useCrNotJoinedMutation } from '@/queries/events'

export const CRPresenceAlert: React.FC<{
  eventId: string
  form: CrFormHandle
}> = ({ eventId, form }) => {
  const crJoined = form.values.sa?.cr_joined ?? null
  const crJoinedError = form.errors['sa.cr_joined']

  // Once "No" is chosen the choice is locked (read-only) — the selection stays visible
  // but can't be changed; Changes are only allowed by marking as resolved.
  const locked = crJoined === false

  const { mutateAsync: notifyCrOperations, isLoading: isNotifying } =
    useCrNotJoinedMutation(eventId, {
      onError: () => {
        showNotification({
          title: 'Unable to notify CR Operations',
          message: 'Something went wrong. Please try again.',
          color: 'red',
        })
      },
    })

  const alertCrOperations = async (resolved: boolean) => {
    try {
      await notifyCrOperations({ resolved })
    } catch {
      return
    }

    form.setFieldValue('sa.cr_joined', resolved)
    showNotification({
      title: resolved ? 'Marked as Resolved' : 'Court Reporter Not Present',
      message: resolved
        ? 'Marked as Resolved. You may now continue completing the form.'
        : 'Court Reporter Operations have been notified. Please await further guidance or assistance.',
      color: resolved ? 'green' : 'orange',
    })
  }

  return (
    <Alert color="orange" title="Confirm Court Reporter Presence">
      <Text size="sm" mb="xs">
        If the Court Reporter has not joined, select{' '}
        <Text component="span" fw={600}>
          No
        </Text>{' '}
        <Text component="span" italic>
          immediately
        </Text>
        .
      </Text>
      <Text size="sm" fw={600}>
        Has the Court Reporter joined the event?{' '}
        <Text component="span" color="red">
          *
        </Text>
      </Text>
      <Group
        spacing="sm"
        mt="xs"
        align="center"
        data-invalid={crJoinedError ? 'true' : undefined}
      >
        <Button
          variant={crJoined === true ? 'filled' : 'default'}
          aria-disabled={locked}
          disabled={isNotifying}
          sx={locked ? { cursor: 'not-allowed' } : undefined}
          onClick={
            locked ? undefined : () => form.setFieldValue('sa.cr_joined', true)
          }
        >
          Yes
        </Button>
        <Button
          variant={crJoined === false ? 'filled' : 'default'}
          aria-disabled={locked}
          disabled={isNotifying}
          loading={isNotifying && crJoined !== false}
          sx={locked ? { cursor: 'not-allowed' } : undefined}
          onClick={
            locked
              ? undefined
              : () => {
                  void alertCrOperations(false)
                }
          }
        >
          No
        </Button>
      </Group>
      {crJoinedError && (
        <Text size="sm" color="red" mt={4}>
          {crJoinedError}
        </Text>
      )}
      {crJoined === false && (
        <Alert color="red" title="Court Reporter has not joined" mt="xs">
          <Stack spacing="sm" align="flex-start">
            <Text size="sm">
              Our Event Support Team has been alerted.
              <br />
              Once the Court Reporter appears, use the ‘Mark as Resolved’ button
              below.
            </Text>
            <Button
              color="red"
              loading={isNotifying}
              disabled={isNotifying}
              onClick={() => {
                void alertCrOperations(true)
              }}
            >
              Mark as Resolved
            </Button>
          </Stack>
        </Alert>
      )}
    </Alert>
  )
}
